import React, { useEffect, useRef } from 'react';
import { AccessibilityInfo, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../Texte';
import { LinearGradient } from 'expo-linear-gradient';
import { Button } from '../Button';
import { CadrePleinEcran } from '../CadrePleinEcran';
import { ShieldCheck, X } from '../icons';
import { BandeauOffre } from './BandeauOffre';
import { BarreProgression } from './BarreProgression';
import { AnneauProgression } from './AnneauProgression';
import { LueurFloue } from './LueurFloue';
import { EtiquettesMotsCles } from './EtiquettesMotsCles';
import { FilEtapes, type Etape, type Etat } from './FilEtapes';
import { t } from '../../i18n';
import { colors, radius, screenPadding, spacing, typography } from '../../theme/tokens';
import type { DossierFinal, EtapeAnalyse, EtapeCv, EtapeLettre, Offre } from '../../types/offres';

export type { Etape, Etat };
export type Blocage = 'quota' | 'cv_manquant' | 'offre_retiree' | 'profil_trop_eloigne';

export const ETAPES: Etape[] = ['analyse', 'cv', 'lettre', 'controle'];

export interface PreparationVueProps {
  offre: Offre;
  etats: Record<Etape, Etat>;
  analyse: EtapeAnalyse | null;
  cv: EtapeCv | null;
  lettre: EtapeLettre | null;
  final: DossierFinal | null;
  blocage: Blocage | null;
  raison: string | null;
  echec: boolean;
  onFermer: () => void;
  onReessayer: () => void;
  onVerifier: () => void;
  onRetourOffres: () => void;
}

/**
 * Écran 4 — l'habillage seulement. Tout ce qui s'affiche vient de l'état que le conteneur
 * (PreparationDossier) tient des réponses du serveur : une étape ne se coche qu'à son vrai retour,
 * les mots-clés sont ceux de l'analyse, aucune durée n'est annoncée.
 */
export function PreparationVue(p: PreparationVueProps) {
  const { offre, etats, analyse, cv, lettre, final, blocage, raison, echec } = p;
  const terminees = ETAPES.filter((e) => etats[e] === 'fait').length;

  const detail = (e: Etape): string => {
    if (etats[e] === 'attente') return t('offres.preparation.attente');
    if (etats[e] === 'en_cours') return t('offres.preparation.enCours');
    if (etats[e] === 'erreur') return t('offres.preparation.echecEtape');
    if (e === 'analyse' && analyse) {
      return t('offres.preparation.analyseFaite', {
        motsCles: t('offres.preparation.motsCles', { count: analyse.motsCles }),
        questions: t('offres.preparation.questions', { count: analyse.questions }),
      });
    }
    if (e === 'cv' && cv) return t('offres.preparation.cvFait', { count: cv.motsClesCouverts }) + (cv.titreAligne ? t('offres.preparation.titreAligne') : '');
    if (e === 'lettre' && lettre) return t('offres.preparation.lettreFaite', { count: lettre.mots });
    if (e === 'controle' && final) return t('offres.preparation.controleFait', { count: final.controle.points.length });
    return '';
  };

  // Chaque changement d'étape est annoncé au lecteur d'écran, avec son résultat quand il y en a un.
  const avant = useRef(etats);
  useEffect(() => {
    for (const e of ETAPES) {
      if (avant.current[e] !== etats[e] && etats[e] !== 'attente') {
        AccessibilityInfo.announceForAccessibility(`${t(`offres.preparation.etapes.${e}`)} : ${detail(e)}`);
      }
    }
    avant.current = etats;
    // `detail` lit l'état du même rendu : seule la transition des étapes doit déclencher l'annonce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etats]);

  const termes = analyse?.termes ?? null;
  const enCours = !final && !blocage && !echec;

  return (
    <CadrePleinEcran fond={colors.night} testID="preparation-dossier">
      <LinearGradient colors={[colors.night, colors.navyDeep]} style={StyleSheet.absoluteFill} pointerEvents="none" />
      {/* Lueurs floues, bleu électrique très atténué ; aucune en haut à droite, derrière le ✕. */}
      <View style={styles.decor} pointerEvents="none">
        <LueurFloue id="lueurGauche" taille={320} bottom={-120} left={-140} />
        <LueurFloue id="lueurDroite" taille={260} top={260} right={-150} />
      </View>

      <ScrollView contentContainerStyle={styles.contenu} showsVerticalScrollIndicator={false}>
        <AnneauProgression faites={terminees} total={ETAPES.length} actif={enCours} fini={!!final} />
        <Text style={styles.titre} accessibilityRole="header">
          {final ? t('offres.preparation.pret') : t('offres.preparation.titre')}
        </Text>
        <BandeauOffre offre={offre} />
        <BarreProgression faites={terminees} total={ETAPES.length} etiquette={t('offres.preparation.progression')} />
        <FilEtapes etats={etats} detail={detail} />
        {termes ? <EtiquettesMotsCles termes={termes} couverts={cv?.couverts ?? null} synthese={!!final} /> : null}

        {/* Sans étiquettes (serveur d'avant les termes), la couverture garde sa ligne. */}
        {final && !termes ? (
          <Text style={styles.couverture}>
            {t('offres.preparation.couverture', {
              couverts: final.controle.couverture.couverts.length,
              total: final.controle.couverture.couverts.length + final.controle.couverture.manquants.length,
            })}
          </Text>
        ) : null}
        {blocage ? <Text style={styles.message}>{t(`offres.preparation.erreurs.${blocage}`)}</Text> : null}
        {blocage === 'profil_trop_eloigne' && raison ? <Text style={styles.raison}>{raison}</Text> : null}
        {echec ? <Text style={styles.message}>{t('offres.preparation.erreurs.echec')}</Text> : null}
      </ScrollView>

      {/* Flottant, sans ligne à lui : sur un petit iPhone, chaque ligne compte pour tenir sans défiler. */}
      <Pressable accessibilityRole="button" accessibilityLabel={t('offres.preparation.fermer')} onPress={p.onFermer} style={styles.rond}>
        <X size={22} color={colors.white} />
      </Pressable>

      {/* Ancré en bas : l'action utile reste visible sans défiler, et la promesse aussi. */}
      <View style={styles.pied}>
        {echec ? <Button title={t('offres.preparation.reessayer')} variant="secondary" onPress={p.onReessayer} /> : null}
        {blocage === 'profil_trop_eloigne' ? (
          <Button title={t('offres.preparation.retourOffres')} variant="secondary" onPress={p.onRetourOffres} />
        ) : final ? (
          // Fond blanc, texte bleu nuit : le plus visible sur ce fond sombre, et au-dessus du seuil AA —
          // le bleu primaire avec texte blanc n'y atteint que 3,3:1.
          <Button title={t('offres.preparation.verifier')} variant="secondary" onPress={p.onVerifier} />
        ) : blocage ? (
          <Button title={t('offres.preparation.terminer')} variant="secondary" onPress={p.onFermer} />
        ) : null}
        <View style={styles.promesse}>
          <ShieldCheck size={18} color={colors.grey200} />
          <Text style={styles.promesseTexte}>{t('offres.preparation.pied')}</Text>
        </View>
      </View>
    </CadrePleinEcran>
  );
}

const styles = StyleSheet.create({
  decor: { ...StyleSheet.absoluteFillObject, overflow: 'hidden' },
  rond: {
    position: 'absolute',
    top: spacing.sm,
    end: screenPadding,
    minWidth: 44,
    minHeight: 44,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.inkSoft,
  },
  contenu: { paddingHorizontal: screenPadding, paddingTop: spacing.sm, paddingBottom: spacing.md, gap: spacing.sm + spacing.xs },
  titre: { textAlign: 'center', fontSize: typography.h2.size, lineHeight: typography.h2.lineHeight, fontWeight: '700', color: colors.white },
  couverture: { textAlign: 'center', fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.white },
  message: { textAlign: 'center', fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.white },
  raison: { textAlign: 'center', fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey200 },
  pied: { gap: spacing.sm, paddingHorizontal: screenPadding, paddingTop: spacing.sm, paddingBottom: spacing.md },
  promesse: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm },
  promesseTexte: { fontSize: typography.small.size, color: colors.grey200 },
});
